/* -----------------Globals--------------- */
import { useCallback, useState } from "react";

import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
/* -----------------Helpers & Hooks--------------- */
import { useAppStore } from "@hooks/useAppStore";

/* -----------------Types--------------- */
import type { Stat } from "components/RowStats";

import { isScreenshotMode } from "@constants/common";
import { mockedProfileDetails } from "@constants/mocked-data";
import { getParentMetadata } from "@helpers/content/metadata";
import useClerkUser from "@hooks/useClerkUser";
import type { MixedItemDist } from "@memorang/types/src/tag";
import { getMixedItemDistribution } from "api/queries";
import { useFocusEffect } from "expo-router";
import { fetchUserRank } from "../api/queries";

/* -----------------Child components--------------- */
import { getHeaderStats, getStatsSectionData } from "../helpers/profile-data";

type ProfileDetails = {
	headerStats: Stat[];
	statsTabData: ReturnType<typeof getStatsSectionData>;
};

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
const allSettled = (promises: Promise<any>[]) => {
	return Promise.all(
		promises.map((promise) =>
			promise
				.then((value) => ({
					status: "fulfilled",
					value,
				}))
				.catch((reason) => ({
					status: "rejected",
					reason,
				})),
		),
	);
};
const useProfileDetails = () => {
	const [profileDetails, setProfileDetails] = useState<ProfileDetails>();
	const {
		currentExamContext: { examId, projectId, organizationId },
	} = useExamContext();
	const { viewerId } = useAuthContext();
	const exam = useAppStore((store) => store.exam);

	const { userPublicMetadataId } = useClerkUser();

	const { currentExamContext } = useExamContext();
	const { examDate } = currentExamContext;
	const setUpProfileDetails = useCallback(async () => {
		const [allTimeLeaderboard, monthLeaderboard, weekLeaderboard, contents] =
			await allSettled([
				fetchUserRank({
					periodType: "all_time",
					projectId,
					organizationId,
					userPublicMetadataId: userPublicMetadataId,
				}),
				fetchUserRank({
					periodType: "month",
					projectId,
					organizationId,
					userPublicMetadataId: userPublicMetadataId,
				}),
				fetchUserRank({
					periodType: "week",
					projectId,
					organizationId,
					userPublicMetadataId: userPublicMetadataId,
				}),
				...(examId
					? [
							getMixedItemDistribution({
								userPublicMetadataId: userPublicMetadataId,
								examId,
								action: "COMMON_DISTRIBUTION",
							}),
						]
					: []),
			]);

		//@ts-ignore
		const mixedItemDistResponse = contents.value as unknown as MixedItemDist;

		const { distribution } = getParentMetadata(mixedItemDistResponse.contents);

		const { low, high, medium } = distribution;
		const numItemsAnswered = low + high + medium;
		const accuracy = numItemsAnswered
			? Math.round(((high + medium) / numItemsAnswered) * 100)
			: 0;
		//@ts-ignore
		const level = allTimeLeaderboard.value?.level;
		const headerStats = getHeaderStats({
			level,
			accuracy,
			answered: numItemsAnswered,
		});
		const statsTabData = getStatsSectionData({
			selectedBundleContext: currentExamContext,
			selectedDate: examDate!,
			//@ts-ignore
			allTimeLeaderboard: allTimeLeaderboard.value,
			//@ts-ignore
			monthLeaderboard: monthLeaderboard.value,
			//@ts-ignore
			weekLeaderboard: weekLeaderboard.value,
			examEnabled: exam?.enabled,
		});
		const data = {
			headerStats,
			statsTabData: statsTabData,
		};
		setProfileDetails(data);
	}, [
		currentExamContext,
		exam?.enabled,
		examDate,
		examId,
		projectId,
		organizationId,
		userPublicMetadataId,
	]);

	useFocusEffect(
		useCallback(() => {
			if (viewerId && examId) {
				setUpProfileDetails();
			}
		}, [setUpProfileDetails, viewerId, examId]),
	);

	return {
		profileDetails: isScreenshotMode ? mockedProfileDetails : profileDetails,
		setUpProfileDetails,
	};
};

export default useProfileDetails;
